import { useLazyQuery } from '@apollo/client'
import styled from '@emotion/styled'
import {
  Box,
  Chip as MuiChip,
  Grid2 as Grid,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { green, red, yellow } from '@mui/material/colors'
import { spacing, SpacingProps } from '@mui/system'
import { isEmpty, round } from 'lodash'
import { useEffect } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_PRODUCT_DASHBOARD_STATS,
  ProductDashboardOrdering,
  ProductDashboardStatFragment,
  ProductsDashboardFilter,
  ProductsDashboardStatsQuery,
  ProductsDashboardStatsQueryVariables,
  SortDirection,
} from '@/graphql'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import { formatCurrency, formatNumber } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { mockThresholds } from '@/mocks/mockStoresReportTable'

import DeliciousTable, { HeadCell } from '../../components/DeliciousTable'

export interface ChipProps extends SpacingProps {
  status: 'active' | 'inactive'
}

export const Chip = styled(MuiChip)<ChipProps>`
  ${spacing};
  background: ${props =>
    props.status === 'inactive' ? yellow[700] : green[700]};
  color: ${props =>
    props.status === 'active' && props.theme.palette.common.white};
`

function StoresProductTable({ filters }: { filters: ProductsDashboardFilter }) {
  const { t } = useT()

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })

  const { orderBy, order, handleRequestSort } =
    useTableOrder<ProductDashboardOrdering>('totalRevenue', SortDirection.DESC)

  const [
    fetchProductDashboardStatsStores,
    {
      data: productDashboardStatsStores,
      loading: productDashboardStatsStoresLoading,
      error: productDashboardStatsStoresError,
      refetch: refetchProductDashboardStatsStores,
      variables,
    },
  ] = useLazyQuery<
    ProductsDashboardStatsQuery,
    ProductsDashboardStatsQueryVariables
  >(GET_PRODUCT_DASHBOARD_STATS, { notifyOnNetworkStatusChange: true })

  useRefresh(() => {
    if (!isEmpty(variables)) {
      refetchProductDashboardStatsStores()
    }
  })

  useEffect(() => {
    if (filters.startDate && filters.endDate && filters.upcs) {
      fetchProductDashboardStatsStores({
        variables: {
          filters: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            upcs: filters.upcs,
            storeIds: filters.storeIds,
            groupBy: 'store',
          },
          ordering: {
            [orderBy]:
              order === SortDirection.ASC
                ? SortDirection.ASC
                : SortDirection.DESC,
          },
          pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
        },
      })
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.upcs,
    page,
    rowsPerPage,
    orderBy,
    order,
  ])

  function getColor(
    thresholds: number[],
    value?: number | null,
    reverse = false,
  ): string | undefined {
    if (value === undefined || value === null) {
      return undefined
    }
    if (reverse) {
      if (thresholds.length == 1 && value > thresholds[0]) return red[700]
      return ''
    }

    if (value < thresholds[0]) return red[700]
    if (value < thresholds[1]) return yellow[700]
    return green[700]
  }

  const headCells: HeadCell<ProductDashboardOrdering>[] = [
    {
      id: 'name',
      alignment: 'left',
      label: t('common.store'),
      sortKey: 'name',
    },
    {
      id: 'totalRevenue',
      alignment: 'right',
      label: t('common.totalRevenue'),
      sortKey: 'totalRevenue',
    },
    {
      id: 'margin',
      alignment: 'right',
      label: t('common.margin'),
      sortKey: 'totalEarnings',
    },
    {
      id: 'volume',
      alignment: 'right',
      label: t('common.volume'),
      sortKey: 'volume',
    },
    {
      id: 'facings',
      alignment: 'right',
      label: t('common.facings'),
      sortKey: 'facings',
    },
    {
      id: 'emptyFacings',
      alignment: 'right',
      label: t('common.emptyFacings'),
      sortKey: 'emptyFacings',
    },
    {
      id: 'revenuePerFacing',
      alignment: 'right',
      label: t('common.revenuePerFacing'),
      sortKey: 'revenuePerFacing',
    },
    {
      id: 'earningsPerFacing',
      alignment: 'right',
      label: t('common.earningsPerFacing'),
      sortKey: 'earningsPerFacing',
    },
  ]

  const renderRow = (stat: ProductDashboardStatFragment, index: number) => {
    return (
      <TableRow hover role="checkbox" tabIndex={-1} key={`${stat.id}-${index}`}>
        <TableCell>
          <Box ml={3}>{stat.name || stat.id}</Box>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat.totalRevenue === null || stat.totalRevenue === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.totalRevenue))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            color={getColor(
              mockThresholds.emptyFacings,
              stat?.emptyFacings,
              true,
            )}
          >
            {stat?.totalEarnings === null || stat?.totalEarnings === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.totalEarnings))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.volume === null || stat?.volume === undefined
              ? t('storesReportTable.noData')
              : formatNumber(round(stat.volume))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.facings === null || stat?.facings === undefined
              ? t('storesReportTable.noData')
              : formatNumber(round(stat.facings))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.emptyFacings === null || stat?.emptyFacings === undefined
              ? t('storesReportTable.noData')
              : formatNumber(round(stat.emptyFacings))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.revenuePerFacing === null ||
            stat?.revenuePerFacing === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.revenuePerFacing))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.earningsPerFacing === null ||
            stat?.earningsPerFacing === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.earningsPerFacing))}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <Stack spacing={4}>
        <Grid container spacing={6}>
          <Grid size={12}>
            <DeliciousTable<
              ProductDashboardStatFragment,
              ProductDashboardOrdering
            >
              data={
                productDashboardStatsStores?.productsDashboardStats?.results ||
                []
              }
              headCells={headCells}
              renderRow={renderRow}
              order={order === 'ASC' ? 'asc' : 'desc'}
              orderBy={orderBy}
              handleRequestSort={handleRequestSort}
              loading={productDashboardStatsStoresLoading}
              error={productDashboardStatsStoresError?.message}
              totalCount={
                productDashboardStatsStores?.productsDashboardStats
                  ?.totalCount || 0
              }
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              title={t('storesReportTable.stores')}
            />
          </Grid>
        </Grid>
      </Stack>
    </>
  )
}

export default StoresProductTable
